import { useCallback, useMemo } from "react";

import { FormRowField, FormikForm, LandingPageFieldSelect, Switch, optionize } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useLandingPageSettings } from "../../../LandingPageSettingsContext";
import { FormBody, Header, Heading, PageContainer, StyledForm, SubHeading } from "../../shared";
import { useFilterSettings, type FilterType } from "../FilterSettingsContext";

type FormValues = {
  type?: FilterType;
};

const filters: Array<FilterType> = ["tags"];

export const FilterTypePage = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const { type, setType } = useFilterSettings();

  const { hasFilterSet, isSuperUser, setDevFetchAllUsersModules, devFetchAllUsersModules } = useLandingPageSettings();

  const initialValues: FormValues = useMemo(() => ({ type: type }), [type]);

  const handleSubmit = useCallback((values: FormValues) => setType(values.type), [setType]);

  const options = optionize(filters.filter((filter) => !hasFilterSet(filter)));

  return (
    <PageContainer>
      <Header>
        <Heading>{formatMessage("heading")}</Heading>
        <SubHeading>{formatMessage("subHeading")}</SubHeading>
      </Header>
      {isSuperUser && (
        <div data-test="show-all-modules-container">
          <Switch
            label={formatMessage("showAllModulesLabel")}
            value={devFetchAllUsersModules}
            onChange={(e) => setDevFetchAllUsersModules(e.target.checked)}
          />
        </div>
      )}
      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnBlur
        formatStrings={formatStrings}
        dataTest="filter-type-form"
      >
        <StyledForm>
          <FormBody>
            <FormRowField<FormValues> name="type">
              <LandingPageFieldSelect options={options} />
            </FormRowField>
          </FormBody>
        </StyledForm>
      </FormikForm>
    </PageContainer>
  );
};

const formatStrings = {
  heading: {
    en: "Module Filtering",
  },
  subHeading: {
    en: "Filter Details",
  },
  typeLabel: {
    en: "What kind of filter would you like to add?",
  },
  typeDescription: {
    en: "A filter allows the list of shown modules to be narrowed according to the kind of filter.",
  },
  typePlaceholder: {
    en: "Choose a filter...",
  },
  showAllModulesLabel: {
    en: "Show modules from all users (DEV)",
  },
} satisfies FormatMessages;
